import { create } from 'zustand';
import { ComponentFactory } from './ComponentRegistry';
import uuid from 'uuid';
import useProjectStore from './useProjectStore';
import {
  createFormRedirectLink,
  createLoopLink,
  createSplitConvergeLink,
  EDGE_TYPES,
} from '../../utils/CanvasConstants';

const useProgressViewStore = create((set, get) => ({
  download: undefined,
  template: undefined,
  project_id: '',
  project_name: '',
  last_modified: undefined,
  components: [],
  project_description: '',
  imagePath: undefined,
  projectStatus: undefined,
  canvas: undefined,
  draftVersion: undefined,
  publishedVersion: undefined,

  zoom_level: 75,
  active_diagram: {},
  locked: true,
  dbIds: undefined,

  setWorkflow: workflow => {
    const canvas = get().readDiagram(workflow.components);
    set({
      project_id: workflow.projectId,
      project_name: workflow.name,
      last_modified: workflow.lastModified,
      project_description: workflow.description,
      components: workflow.components,
      canvas,
      imagePath: workflow.imagePath,
      projectStatus: workflow.status,
    });
  },

  createNode: (component, template) => {
    let isFromConditionalPath = false;

    if (component.componentData !== null && component.componentData.isFromConditionalPath) {
      isFromConditionalPath = component.componentData.isFromConditionalPath;
    }

    return {
      id: component.componentId,
      type: 'component',
      data: {
        logo: template.logo,
        hasInput: template.hasInput ? template.hasInput : false,
        hasOutput: template.hasOutput ? template.hasOutput : false,
        type: component.type,
        displayName: template.displayName,
        description: component.description ? component.description : '',
        next: component.nextComponents,
        label:
          component.componentData && component.componentData.name
            ? component.componentData.name
            : template.displayName,
        baseColor: template.baseColor,
        fromWorkflowPath: isFromConditionalPath,
      },
      position: { x: component.viewData.x, y: component.viewData.y },
    };
  },

  createLink: (newNodeId, nextId, baseColor, isLoop = false, maxIterations = null, edgeCondition = null) => {
    if (edgeCondition != null) {
      return {
        id: uuid(),
        source: newNodeId,
        target: nextId,
        markerEnd: { type: 'arrowclosed', color: edgeCondition.isDefault ? '#999999' : '#F5A623' },
        type: EDGE_TYPES.GATEWAYLINK,
        label: 'gatewayLink',
        data: { showDeleteButton: false, edgeCondition: edgeCondition },
        style: {
          strokeWidth: '3px',
          stroke: edgeCondition.isDefault ? '#999999' : '#F5A623',
          ...(edgeCondition.isDefault ? { strokeDasharray: '5,5' } : {}),
        },
      };
    }

    if (isLoop) {
      return {
        id: uuid(),
        source: newNodeId,
        target: nextId,
        markerEnd: { type: 'arrowclosed', color: '#9c27b0' },
        type: EDGE_TYPES.LOOPLINK,
        label: 'loopLink',
        data: { showDeleteButton: false, isLoop: true, maxIterations: maxIterations },
        style: { strokeWidth: '3px', stroke: '#9c27b0', strokeDasharray: '5,5' },
      };
    }

    return {
      id: uuid(),
      source: newNodeId,
      target: nextId,
      markerEnd: { type: 'arrowclosed', color: '#868686' },
      type: EDGE_TYPES.LINK,
      style: { strokeWidth: '3px', stroke: baseColor },
    };
  },

  readDiagram: components => {
    let diagram = [];
    let dbIds = new Map([]);
    let compNextMap = new Map([]);

    if (components) {
      components.map((component, id) => {
        const data = component.componentData?.data?.[0];
        const startingLoopComponentId = data?.targetComponentId;
        const targetDatabaseId =
          component.componentData?.targetDatabaseId || data?.targetDatabaseId;
        const redirectsToAnotherForm = data?.linkedComponent;
        if (ComponentFactory[component.type] !== undefined) {
          const newNode = get().createNode(component, ComponentFactory[component.type]);
          if (startingLoopComponentId) {
            diagram.push(createLoopLink(component.componentId, startingLoopComponentId));
          }

          if (redirectsToAnotherForm) {
            const componentIdsToRedirect = data?.linkedComponents;
            if (componentIdsToRedirect) {
              componentIdsToRedirect.forEach(id => {
                diagram.push(createFormRedirectLink(component.componentId, id));
              });
            }
          }

          if (targetDatabaseId) {
            useProjectStore
              .getState()
              .createDbNodeLogic(component, dbIds, diagram, targetDatabaseId);
            newNode.data.hasDb = true;
          }

          diagram.push(newNode);

          if (newNode.data.next && newNode.data.next.length != 0) {
            newNode.data.next.map((next, nextId) => {
              const newLink = get().createLink(
                newNode.id,
                next.componentId,
                newNode.data.baseColor,
                next.isLoop || false,
                next.maxIterations || null,
                next.edgeCondition || null
              );
              if (newNode.data.type === 'conditional_workflow_path') {
                compNextMap.set(next.componentId, newNode.id);
              }
              diagram.push(newLink);
            });
          }
        }
      });
    }
    set({ dbIds });
    compNextMap.forEach((value, key) => {
      let conditional_workflow = diagram.find(obj => obj.id === key);
      if (conditional_workflow) {
        conditional_workflow.data.parentId = value;
      }
    });

    // Create split-converge links for loop_through_list -> converge_threads pairs
    const loopThroughListNodes = diagram.filter(n => n.data?.type === 'loop_through_list');
    const convergeThreadsNodes = diagram.filter(n => n.data?.type === 'converge_threads');

    if (loopThroughListNodes.length > 0 && convergeThreadsNodes.length > 0) {
      for (const loopNode of loopThroughListNodes) {
        const visited = new Set();
        const queue = [loopNode.id];

        while (queue.length > 0) {
          const currentId = queue.shift();
          if (visited.has(currentId)) continue;
          visited.add(currentId);

          const convergeNode = convergeThreadsNodes.find(n => n.id === currentId);
          if (convergeNode) {
            diagram.push(createSplitConvergeLink(loopNode.id, convergeNode.id));
            break;
          }

          const outgoingLinks = diagram.filter(e => e.type === 'link' && e.source === currentId);
          for (const link of outgoingLinks) {
            if (!visited.has(link.target)) {
              queue.push(link.target);
            }
          }
        }
      }
    }

    return diagram;
  },

  setCanvas: canvas => set({ canvas }),

  clear: () => {
    set({
      project_id: '',
      project_name: '',
      project_description: '',
      components: [],
    });
  },

  getAggregate: () => {
    const s = get();
    return {
      project_id: s.project_id,
      project_name: s.project_name,
      draftVersion: s.draftVersion,
      publishedVersion: s.publishedVersion,
      project_description: s.project_description,
      imagePath: s.imagePath,
      components: s.components ? [...s.components] : [],
    };
  },
}));

export default useProgressViewStore;
